import { SaveTicketValues } from "../../../tickets/creation";
import { DescriptionConfigInput } from "../components/inputs/DescriptionConfigInput";
import { DuedateConfigInput } from "../components/inputs/DuedateConfigInput";
import { IssueConfigInput } from "../components/inputs/IssueConfigInput";
import { MaterialConfigInput } from "../components/inputs/MaterialConfigInput";
import { MediaConfigInput } from "../components/inputs/MediaConfigInput";
import { MetaConfigInput } from "../components/inputs/MetaConfigInput";
import { PriorityConfigInput } from "../components/inputs/PriorityConfigInput";
import { ProjectConfigInput } from "../components/inputs/ProjectConfigInput";
import { SourceConfigInput } from "../components/inputs/SourceConfigInput";
import { StateConfigInput } from "../components/inputs/StateConfigInput";
import { TitleConfigInput } from "../components/inputs/TitleConfigInput";
import { DescriptionFieldConfig } from "./field_configs/Description";
import { DuedateFieldConfig } from "./field_configs/Duedate";
import { IssueFieldConfig } from "./field_configs/Issue";
import { MaterialFieldConfig } from "./field_configs/Material";
import { MediaFieldConfig } from "./field_configs/Media";
import { TicketMetaFieldConfig } from "./field_configs/Meta";
import { PriorityFieldConfig } from "./field_configs/Priority";
import { ProjectFieldConfig } from "./field_configs/Project";
import { SourceFieldConfig } from "./field_configs/Source";
import { TitleFieldConfig } from "./field_configs/Title";

// to add new views, add them to the list and implement the view in the UI
export const AllSaveViews = ["steps", "formular", "compact"] as const;
export type SaveViews = (typeof AllSaveViews)[number];

// define the custom field config map with the correct types for each field
// attributes in those configs need to be optional
export type CustomFieldConfigMap = ValidCustomFieldConfigMap<{
  title: DefaultFieldConfig & TitleFieldConfig;
  description: DefaultFieldConfig & DescriptionFieldConfig;
  priority: DefaultFieldConfig & PriorityFieldConfig;
  media: DefaultFieldConfig & MediaFieldConfig;
  material: DefaultFieldConfig & MaterialFieldConfig;
  issuecategory: DefaultFieldConfig & IssueFieldConfig;
  project: DefaultFieldConfig & ProjectFieldConfig;
  duedate: DefaultFieldConfig & DuedateFieldConfig;
  state: DefaultFieldConfig;
  source: DefaultFieldConfig & SourceFieldConfig;
  assignedTo: DefaultFieldConfig;
  meta: DefaultFieldConfig & TicketMetaFieldConfig;
}>;

// to add new fields, add them to the list and implement the view in the UI
const customFieldConfigMap: Record<keyof CustomFieldConfigMap, true> = {
  title: true,
  description: true,
  priority: true,
  media: true,
  material: true,
  issuecategory: true,
  project: true,
  duedate: true,
  state: true,
  source: true,
  assignedTo: true,
  meta: true,
};

// add the config component that should be loaded for each field
// in this component you have access to the current path via useContext(PathContext)
// in the Form.Item name prop destructure this path and use it to set the correct name for the field
export const ConfigComponentMap: {
  [K in keyof CustomFieldConfigMap]: React.ComponentType<ConfigInputProps<K>>;
} = {
  title: TitleConfigInput,
  description: DescriptionConfigInput,
  priority: PriorityConfigInput,
  media: MediaConfigInput,
  material: MaterialConfigInput,
  issuecategory: IssueConfigInput,
  project: ProjectConfigInput,
  duedate: DuedateConfigInput,
  state: StateConfigInput,
  source: SourceConfigInput,
  assignedTo: null,
  meta: MetaConfigInput,
};

// default config that is used for all fields
export type DefaultFieldConfig = {
  name: ConfigurableField; // used to identify the field
  hidden?: boolean; // used to hide the field in the UI
  label?: string; // used to set the label of the field in the UI
  required?: boolean; // used to set the field as required in the UI
  help?: string; // used to set the help text of the field in the UI
};

// ===== DONT CHANGE HERE =====
export type ConfigurableField = keyof typeof customFieldConfigMap;

// get all configurable fields from the customFieldConfigMap
export const AllConfigurableFields = Object.keys(
  customFieldConfigMap
) as ConfigurableField[];

type ValidCustomFieldConfigMap<
  T extends Partial<Record<keyof SaveTicketValues, DefaultFieldConfig>>,
> = {
  [K in keyof T]: T[K] extends DefaultFieldConfig ? T[K] : never;
};

// Used to flatten config into union
type AllFieldConfigs = {
  [key in keyof SaveTicketValues]: undefined;
};

// This is a union of all field configs that have an actual config implemented
type FieldConfigMap = Omit<AllFieldConfigs, keyof CustomFieldConfigMap> &
  CustomFieldConfigMap;

export type FieldConfigUnion = {
  [K in ConfigurableField]: {
    name: K;
  } & (FieldConfigMap[K] extends undefined ? {} : FieldConfigMap[K]);
}[ConfigurableField];

export type SaveConfig = {
  config: FieldConfigUnion[];
};

export type FieldValueType<T extends ConfigurableField> =
  CustomFieldConfigMap[T] & { name: T };

type WithName<T extends ConfigurableField> = CustomFieldConfigMap[T] & {
  name: T;
};

export type ConfigInputProps<T extends ConfigurableField> = {
  value?: WithName<T>;
  onChange?: (value: WithName<T>) => void;
};

export type FormItemKey<T extends ConfigurableField> = keyof Omit<
  CustomFieldConfigMap[T],
  "name"
>;

export type FormItemPath<T extends ConfigurableField | undefined = undefined> =
  T extends undefined
    ? FormItemPathDefault
    : [...(string | number)[], FormItemKey<T>];

// Get the keys of DefaultFieldConfig to use in the default path
export type DefaultFieldConfigKeys = keyof Omit<DefaultFieldConfig, "name">;

// Default path type when no generic is passed
export type FormItemPathDefault = [DefaultFieldConfigKeys];
